'use client'

/**
 * Documentation section for the HEXIS BODYFENCE paint-protection-film group:
 * /products/hexis-bodyfencex-gloss, /products/hexis-bodyfencexs-satin, and
 * /products/hexis-bodyfencexm-ultra-matte. Three official HEXIS S.A.S. PDFs
 * Peter supplied (scripts/supplier-assets/intake-hexis-bodyfence-docs/),
 * converted to HTML rather than embedded as PDFs or offered as download-only
 * links. Follows the pattern established in 3m-2080.tsx / avery-sw900.tsx.
 * No colour swatch poster was supplied for this group, so this file has no
 * poster/Modal image section.
 *
 * Coverage differs per product, per
 * scripts/supplier-assets/intake-hexis-bodyfence-docs/sources.json:
 * - hexis-bodyfencex-gloss gets all three documents: the Technical Data
 *   Sheet (transcribed BY HAND from `pdftotext -layout
 *   bodyfencex-technical-data-sheet.txt`; this sheet is titled "BODYFENCEX"
 *   only, for the glossy transparent finish), the Warranty, and the
 *   Application Guide.
 * - hexis-bodyfencexs-satin and hexis-bodyfencexm-ultra-matte each get only
 *   the Warranty and Application Guide, plus a short note explaining that no
 *   Satin/Matte-specific technical data sheet was supplied, so the Gloss
 *   sheet's physical film specs (thickness, tensile strength, etc.) are not
 *   applied to these two products.
 * - The Warranty and Application Guide content is identical across all 3
 *   products, so it is factored into two internal, non-exported helper
 *   functions (WarrantySection, ApplicationGuideSection) used by all three
 *   exported components, rather than duplicated JSX.
 *
 * Every figure, unit, and claim is copied exactly from the source text at
 * `pdftotext -layout scripts/supplier-assets/intake-hexis-bodyfence-docs/*.txt`.
 * No em-dash (U+2014) or en-dash (U+2013) was found anywhere in any of the
 * 3 source files (checked by grepping all three for both characters), so no
 * house-style dash substitution was needed in this file.
 *
 * Extraction artifacts found and fixed:
 * (a) In the Application Guide's Required Equipment list, the registered
 *     trademark mark for the adhesive tape was split onto its own line by
 *     the PDF's multi-column layout ("Adhesive tape Tesa 50110" followed by
 *     a lone "&reg;" on the next line). The same product is written
 *     correctly elsewhere in the same document ("Using a TESA&reg; 50110
 *     adhesive tape..." in the Tear-off test section), so the equipment-list
 *     entry is normalized to "Tesa&reg; 50110" here to match.
 * (b) The Warranty's Eligibility Conditions section prints "the chassis
 *     umber of the vehicle", missing the leading "n" of "number" (the line
 *     wraps mid-phrase in the source column layout right at that word).
 *     Corrected to "chassis number" here as a text-layer character drop, the
 *     same class of fix as avery-sw900.tsx's "119 um)Reference" ->
 *     "119 um). Reference" space-insertion.
 * No degree-symbol substitution artifact (the circle-glyph "(degree)F"/"(degree)C"
 * problem documented in 3m-2080.tsx) appears anywhere in these 3 files; "°C"
 * and "°F" print correctly throughout and are copied as-is.
 *
 * Left uncorrected as the source's own text, not an extraction artifact: the
 * Application Guide's Finishing step 4.3 reads "remove as much as humidity
 * as possible" (grammatically awkward, most likely a translation artifact in
 * this French-authored document, not a character substitution or dropped
 * letter). Printed exactly as in the source, same restraint 3m-2080.tsx used
 * for "oreintation" and "preimum"/"apperance".
 *
 * Duplicate omissions (same restraint as 3m-2080.tsx/avery-sw900.tsx):
 * - The Technical Data Sheet's "User's Instructions" bullet "The remaining
 *   film must be stored on its original core (do not cut the rolls into
 *   several lengths or widths, do not repackage the product); the roll must
 *   be closed immediately and properly (masking tape or roll-collars) after
 *   use in order to preserve all its qualities" is a near-duplicate of the
 *   Application Guide's Section 1 Recommendations bullet ("...do not cut the
 *   rolls into several lengths, do not repackage the product); the roll
 *   must be closed immediately and properly after use (tape or roll-
 *   collars), without exerting pressure on the film, in order to preserve
 *   all its qualities"). The Application Guide's wording is kept as
 *   canonical (it appears on all 3 product pages and adds the "without
 *   exerting pressure on the film" detail the Technical Data Sheet lacks);
 *   the Technical Data Sheet's version, and its lone extra word "widths", is
 *   dropped from the Gloss-only Technical Data Sheet transcription.
 * - The Technical Data Sheet's Storage table gives Storage temperature
 *   "+15 (degree)C to +25 (degree)C (+59 (degree)F to +77 (degree)F)" and
 *   Relative humidity "50 %", which duplicate figures already given in the
 *   Application Guide's "Store Your Films Under Appropriate Conditions"
 *   sidebar ("the best temperature ranges from 15 (degree)C to 25 (degree)C
 *   (59 (degree)F to 77 (degree)F) with relative humidity of 50 %"), which
 *   is shown on all 3 product pages. Those two rows are dropped from the
 *   Gloss-only Technical Data Sheet Storage table, keeping only its
 *   non-duplicated rows (storage period, storage area, storage method,
 *   orientation of rolls).
 * - The Technical Data Sheet's closing "NOTES" legal paragraph and the
 *   Application Guide's closing disclaimer paragraph are near-duplicates.
 *   The Application Guide's version (shorter) is kept in full there since it
 *   is shared across all 3 products. The Technical Data Sheet's Notes
 *   section, shown only on the Gloss page, is trimmed to keep just its two
 *   sentences that are not present in the Application Guide's version (the
 *   "request an expert evaluation" and "based on measurements regularly
 *   performed in the laboratory" sentences); the sentences that duplicate
 *   the Application Guide's disclaimer verbatim are dropped from the
 *   Technical Data Sheet section.
 * - The Warranty's Section 2 table lists two groups of films. Only the row
 *   for BODYFENCEX, BODYFENCEXM, BODYFENCEXS, DFENCEXTRM, BODYXFAST,
 *   DFENCEONEX, and DFENCEONEXM (10 years standard / Lifetime with a
 *   BODYFENCE-certified installer) is shown, since that is the group our 3
 *   products belong to. The other row, covering BODYFENCE, BODYFENCEM,
 *   BFBLACK, BFBLACKM and BFWIDE (7 years / 10 years), is not shown, since
 *   none of those film names match our products and nothing beyond what is
 *   printed for that row is known.
 * - The Application Guide's "CONTENTS" table of contents (a numbered list of
 *   its own section titles with page numbers) is omitted: it duplicates the
 *   headings already used as this component's Disclosure titles, and its
 *   dot-leader formatting extracts as a run of replacement characters
 *   rather than real content.
 * - Running headers/footers ("H E XI S", "APPLICATION GUIDE | BODYFENCE",
 *   page fractions like "1/8"..."8/8") are PDF page furniture, not content,
 *   and are not reproduced; each document's control-number code (e.g.
 *   "FPP.FPS.006 I 09.25") is kept once, as a caption under its heading,
 *   matching the "Revision 20, dated 03/10/26" caption convention in
 *   avery-sw900.tsx.
 * - The Warranty PDF's closing "HEAD OFFICE / CONTACT INFORMATION" postal
 *   address block is letterhead, not documentation content, and is omitted.
 *
 * No figure, unit, or word was otherwise added, removed, or rounded.
 *
 * See scripts/supplier-assets/intake-hexis-bodyfence-docs/sources.json for
 * full provenance.
 */
import { Disclosure, DisclosureGroup } from '@heroui/react'
import type { ReactNode } from 'react'

import { Button } from '@/components/ui/button'

// ---------------------------------------------------------------------
// Presentational helpers (same shape as 3m-2080.tsx / avery-sw900.tsx)
// ---------------------------------------------------------------------

function KeyValueTable({ rows }: { rows: Array<{ label: string; value: ReactNode }> }) {
  return (
    <div className='rounded-lg border border-divider overflow-hidden'>
      {rows.map((row, i) => (
        <div
          key={row.label}
          className={`flex flex-col gap-1 px-4 py-2.5 text-sm sm:flex-row sm:gap-6 ${i % 2 === 0 ? 'bg-overlay' : ''}`}
        >
          <span className='text-muted font-medium shrink-0 sm:w-2/5'>{row.label}</span>
          <span className='text-foreground'>{row.value}</span>
        </div>
      ))}
    </div>
  )
}

function BulletList({ items }: { items: ReactNode[] }) {
  return (
    <ul className='list-disc space-y-1.5 pl-5 text-foreground/80'>
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  )
}

interface DocSectionProps {
  id: string
  title: string
  children: ReactNode
}

function DocSection({ id, title, children }: DocSectionProps) {
  return (
    <Disclosure id={id} aria-label={title} className='overflow-hidden rounded-lg border border-divider bg-surface'>
      <Disclosure.Heading>
        {/* transform-none: HeroUI scales a pressed button to 0.97, which on a
            full-width trigger inside an overflow-hidden card leaves a sliver of
            card showing around the pressed fill. cw-ui-heading: the trigger
            sits in the Disclosure's h3, which the theme sets in Anton; the
            title stays in the body face (spec 3.2 component-heading opt-out). */}
        <Button
          slot='trigger'
          variant='ghost'
          className='h-auto w-full justify-between rounded-none px-4 py-3.5 text-left transform-none'
        >
          <span className='cw-ui-heading text-base font-semibold text-foreground'>{title}</span>
          <Disclosure.Indicator className='text-muted' />
        </Button>
      </Disclosure.Heading>
      <Disclosure.Content>
        <Disclosure.Body className='space-y-3 border-t border-divider px-4 py-4 text-sm text-foreground/80'>
          {children}
        </Disclosure.Body>
      </Disclosure.Content>
    </Disclosure>
  )
}

// ---------------------------------------------------------------------
// Warranty: shared across all 3 products (bodyfence-warranty.txt,
// document AMC.DOC.35D 04.24)
// ---------------------------------------------------------------------

function WarrantySection() {
  return (
    <div>
      <h3 className='text-base font-semibold text-foreground mb-3'>Warranty</h3>
      <p className='text-xs text-muted mb-3'>Document AMC.DOC.35D 04.24</p>
      <p className='text-sm text-foreground/80 mb-3'>
        The HEXIS “BODYFENCE” warranty applies to the BODYFENCE range of products (excluding accessories), which are
        used, transformed and implemented in accordance with the requirements stated in the HEXIS technical data
        sheets and application guides available on our website www.hexis-graphics.com. This warranty applies
        exclusively after online registration on our website within 30 days following the purchase of a protection
        film of the BODYFENCE range. This warranty starts from the purchase date of the protective film of the
        BODYFENCE range (installer invoice as a proof). This warranty exclusively covers the following faults:
        yellowing and cracking of the product.
      </p>
      <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
        <DocSection id='w-eligibility' title='1. Eligibility Conditions'>
          <BulletList
            items={[
              'This warranty applies to any individual purchaser’s personal vehicle on which a protective film of the BODYFENCE range is installed.',
              'To be valid, the warranty requires that the purchasers register the vehicle online on the dedicated website, within 30 days following the product purchase date.',
              'They must specify the purchase date of the protection film of the BODYFENCE range, the name of the installer, the invoice number of the installation, and the chassis number of the vehicle.',
              'The film must be installed by a professional (an installation that has been performed by a BODYFENCE certified installer increases the duration of this warranty). Any installation carried out by a private individual will not be covered by this warranty.',
              'The installation must be performed on the original paint and the purchaser must follow the maintenance requirements for the BODYFENCE protection films given in our application guides available on our website www.hexis-graphics.com.',
              'This warranty is not transferable. Warranty coverage is only applicable to the original purchaser of the product. This warranty coverage definitely ends in case of selling or processing of the vehicle.'
            ]}
          />
        </DocSection>

        <DocSection id='w-period' title='2. Warranty Period'>
          <p>
            HEXIS S.A.S. warrant their BODYFENCE range films (upon reference) for a duration of 7 years up to a
            lifetime warranty. The warranty period varies according to the film and according to the HEXIS
            certification or non-certification of the installer.
          </p>
          <p className='text-xs text-muted'>
            Scoped to the films this product belongs to; the Warranty document covers other BODYFENCE-range films
            under a different row of the same table, not reproduced here.
          </p>
          <KeyValueTable
            rows={[
              {
                label: 'BODYFENCEX, BODYFENCEXM, BODYFENCEXS, DFENCEXTRM, BODYXFAST, DFENCEONEX, DFENCEONEXM',
                value: '10 years'
              },
              {
                label:
                  'BODYFENCEX, BODYFENCEXM, BODYFENCEXS, DFENCEXTRM, BODYXFAST, DFENCEONEX, DFENCEONEXM (BODYFENCE certified installer)',
                value: 'Lifetime'
              }
            ]}
          />
          <p>These warranty durations are valid regardless of the product exposure (both vertical and horizontal).</p>
        </DocSection>

        <DocSection id='w-warranted' title='3. What Is Warranted'>
          <BulletList
            items={[
              'This warranty covers the following faults: yellowing and cracking of the product due to imperfections in material or manufacture.',
              'The warranty applies exclusively to films installed on automotive vehicles used on roads suitable for public traffic.',
              'If the product is found to be faulty, HEXIS commit themselves to replace the respective product free of charge.',
              'The product will be, at the discretion of the purchaser, delivered, either to the purchaser or to an installer whose contact address will be communicated to us.'
            ]}
          />
        </DocSection>

        <DocSection id='w-not-warranted' title='4. What Is Not Warranted'>
          <p>
            The blemishes of the installed film are not taken into account in this warranty. Only the installer of a
            protective film of the BODYFENCE range is responsible for the aspect of the product once installed.
          </p>
          <p>
            If, before installation, the professional notes a visual fault in the film or adhesive, it is his
            responsibility not to install it. The installation by a private individual is excluded from this
            warranty.
          </p>
          <p>Within the scope of this warranty, HEXIS do not warrant the following parameters:</p>
          <BulletList
            items={[
              'the film installation or removal duration;',
              'the substrate and the possible damages which it could have undergone;',
              'the substrate recovery;',
              'any lifting of the film occurred within the 14 days following the installation, due to vehicle cleaning carried out with any kind of detergent;',
              'scratches and all type of damages caused to the film after having been cleaned in a car wash.'
            ]}
          />
          <p>HEXIS commit themselves to replace the products that they accept as being faulty.</p>
        </DocSection>

        <DocSection id='w-limitations' title='5. Limitations'>
          <p>
            The HEXIS BODYFENCE warranty applies to the BODYFENCE range films that have been installed by
            professionals. In no case, this warranty can apply to an installation carried out by a private
            individual.
          </p>
          <p>
            The film must imperatively be cleaned with BODYRELOAD and BODYFINISH maintenance products, at least every
            four months, following the instructions described in the latest version of our application guides
            available on our website www.hexis-graphics.com.
          </p>
          <p>
            Climatic effect: the degradation of materials is a natural and unavoidable phenomenon. A slight and
            progressive modification of the transparency, colour and gloss, as well as a slight peeling off at the
            edges and around the rivets are normal phenomena that do not fall within the scope of this warranty.
          </p>
          <p>
            A usage of our products that is not in accordance with the indications defined in the technical data
            sheets and application guides, involves the responsibility of the installer for any risk arising from
            this usage or application. In the case of usage that is not in accordance with the specifications in the
            technical data sheets, application guides and maintenance requirements, it is the responsibility of the
            installer to determine the compatibility between the HEXIS product and the application, and the risks
            and/or obligations related with this installation. In no case, the end user can address to HEXIS, under
            no circumstances, a claim or compensation request within the scope of this warranty program.
          </p>
        </DocSection>
      </DisclosureGroup>
    </div>
  )
}

// ---------------------------------------------------------------------
// Application Guide: shared across all 3 products
// (bodyfence-fpp-spec-sheet.txt, document FPP.FPS.006 I 09.25)
// ---------------------------------------------------------------------

function ApplicationGuideSection() {
  return (
    <div>
      <h3 className='text-base font-semibold text-foreground mb-3'>Application Guide</h3>
      <p className='text-xs text-muted mb-3'>Document FPP.FPS.006 I 09.25, protection film: BODYFENCE range</p>
      <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
        <DocSection id='ag-required-equipment' title='Required Equipment'>
          <BulletList
            items={[
              'Adhesive tape Tesa® 50110',
              '1-litre PULVERISAT vaporiser',
              '19-litre SHAGSPRAY vaporiser',
              'HEXISOAP application solution to be diluted',
              'STEAMERSC3 steam cleaner',
              'HEXISGEL application liquid',
              'Demineralised water',
              'SHAGFENCEM or SHAGPAM squeegee',
              'CUTOLFA30 30° blade cutter',
              'LAMOLFA30 refill',
              <>
                Liquids for the cleaning of application surfaces:
                <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                  <li>SHAGREMOV</li>
                  <li>SHAGCLEAN</li>
                </ul>
              </>,
              'ProTech® SHAMPCARV2 car shampoo',
              'Soft cloth',
              'BODYFINISH cleaning kit',
              'CERAMIC COATING KIT NANOSERUM BY HEXIS'
            ]}
          />
        </DocSection>

        <DocSection id='ag-features' title='Product Range & Features'>
          <p>
            The Bodyfence films are specially developed for protecting car bodywork. Its excellent shock-absorbing
            properties protect your bodywork against chips caused by stones, insects, car doors, washing brushes,
            etc. They also provide an excellent protection against vandalism.
          </p>
          <p>
            <span className='font-medium text-foreground'>BODYFENCE:</span> BODYFENCE is a transparent
            adhesive-coated film with improved surface sliding in comparison with PVC films (CAR100B).
          </p>
          <p>
            <span className='font-medium text-foreground'>BODYFENCEX:</span> This transparent film is self-healing
            at ambient temperature. Its wider version, called BFWIDE, is specially developed for protecting
            large-sized car bonnets. DFENCEXTRM is thicker and specially developed for protecting the front side of
            vehicles. In comparison, the DFENCEONEX film is thinner and particularly suitable for protecting a car’s
            interior surfaces (excluding textiles and surfaces incompatible with wet application).
          </p>
          <p>
            <span className='font-medium text-foreground'>BODYXFAST:</span> The transparent, ultra-flexible product
            is self-healing at ambient temperature and specially developed for quicker and easier film application.
          </p>
          <p>
            <span className='font-medium text-foreground'>BFBLACK:</span> This black film is self-healing at ambient
            temperature.
          </p>
          <p>They are available in glossy, matt or satin surface finish, upon product code.</p>
          <p className='text-xs text-muted'>
            Application methods are based upon HEXIS’ experience and are non-restrictive. Comply with instructions to
            ease application of HEXIS films. The illustrations in this document are representative and not binding;
            the accessories and products used may differ depending on technical and commercial developments. HEXIS
            also offer training sessions for beginners and professionals to achieve optimum results.
          </p>
        </DocSection>

        <DocSection id='ag-preparing-surface' title='Preparing Your Application Surface'>
          <p>
            Your BODYFENCE film can be applied to any vehicle body as long as the target surface is clean, smooth,
            non-porous and free from any traces of oil, grease, wax, silicone or other contaminants. To avoid
            unexpected outcomes, always assume that every substrate is dirty and needs to be cleaned.
          </p>
          <p>
            It is recommended to carry out a preliminary trial on a small, inconspicuous surface area of the
            substrate to check the compatibility between the different elements.
          </p>
          <p className='text-xs text-muted'>
            For further technical information on the BODYFENCE film, please refer to the technical data sheet
            available on our website at www.hexis-graphics.com.
          </p>
        </DocSection>

        <DocSection id='ag-storage' title='Store Your Films Under Appropriate Conditions'>
          <p>
            Keep the films away from any major source of heat (radiators and heaters, direct exposure to sunlight,
            etc.): the best temperature ranges from 15 °C to 25 °C (59 °F to 77 °F) with relative humidity of 50 %.
          </p>
          <p>
            Store your films in their original packaging, in a dry environment. The rolls are wrapped in sealed
            polyethylene bags and are maintained in their cardboard packaging by plastic suspension caps to prevent
            the windings from being crushed by their own weight.
          </p>
          <p>Always store the rolls in an upright position.</p>
        </DocSection>

        <DocSection id='ag-1-recommendations' title='1. Recommendations'>
          <BulletList
            items={[
              'Optimal adherence of the BODYFENCE films is obtained after 24 hours following application.',
              'The BODYFENCE films are usually used on the vehicle body areas the most exposed to aggression (bumper, bonnet, doors, etc.), however, they may also be used on the entire car surface!',
              'After cutting the necessary film length for the wrapping project, the remaining film must be stored on its original core (do not cut the rolls into several lengths, do not repackage the product); the roll must be closed immediately and properly after use (tape or roll-collars), without exerting pressure on the film, in order to preserve all its qualities.'
            ]}
          />
        </DocSection>

        <DocSection id='ag-2-preliminary-test' title='2. Preliminary Test of the Application Surfaces'>
          <p>
            Before proceeding with any application, the installer must first inspect the substrate and the paint to
            which the film will be applied.
          </p>
          <p>The installer and the client are responsible for the suitability assessment of the target surface to be covered.</p>

          <p className='font-medium text-foreground'>2.1. Preliminary inspection of the substrate</p>
          <BulletList
            items={[
              'Any fresh new paint must have dried for at least 7 days at 25 °C (77 °F) to outgas completely. An outgassing test must be carried out before applying the film.',
              'Any old, powdery or flaky paint must be sanded and renewed before application and must undergo a tear-off test.'
            ]}
          />

          <p className='font-medium text-foreground'>2.2. Tear-off test</p>
          <p>
            Using a TESA® 50110 adhesive tape, or similar, apply it to a surface of 2.5 cm x 5 cm (1 in. x 2 in.) plus
            some overhang material for easier removal. Fold and tear it off with one quick pull perpendicular to the
            substrate surface. The film must generate a certain resistance during its removal. Repeat this process in
            several places.
          </p>
          <p className='text-xs text-muted'>
            On request, HEXIS can provide you with a Tesa® adhesive tape in 2.5 cm x 5 cm (1 in. x 2 in.) size. HEXIS
            cannot be held liable for any damage to the substrate following the execution of this test.
          </p>

          <p className='font-medium text-foreground'>2.3. Outgassing test</p>
          <p>
            Use a square piece of around 15 cm x 15 cm (6 in. x 6 in.) of self-adhesive polyester or of the film to be
            applied. Wait for 24 hours or 2 hours at 65 °C (149 °F). The appearance of bubbles indicates that the
            substrate has insufficiently outgassed. Therefore, this process should be repeated after a couple of
            days; or else the procedure described below should be carried out.
          </p>

          <p className='font-medium text-foreground'>2.4. Outgassing procedure with flame treatment</p>
          <p className='text-xs text-muted'>(Polycarbonate, translucent or diffusing methacrylate, expanded PVC, etc.)</p>
          <p>
            This method consists of changing the surface tension of a substrate by swiping it with the flame of a gas
            burner. Using the flame’s blue tip, proceed evenly with fast sweeps horizontally and vertically along the
            whole substrate surface.
          </p>
          <p className='font-semibold text-foreground'>
            MOVE THE FLAME IN SWIPING MOTIONS ON THE SUBSTRATE (RISK OF DESTROYING THE SUBSTRATE IF A FIXED POINT IS
            HEATED MORE THAN A SECOND).
          </p>
          <p>
            The film must be applied right after that treatment as this light surface treatment disappears after a
            few minutes.
          </p>
          <p className='text-xs text-muted'>HEXIS are not liable for any bubbles caused by outgassing.</p>
        </DocSection>

        <DocSection id='ag-3-cleaning' title='3. Cleaning'>
          <p>
            Cleaning of the substrate is required before performing the application. It should always be assumed
            that the substrate is contaminated with dirt. Some residues or contaminants may not be visible; however,
            they may impact the adherence of the film.
          </p>
          <p className='text-xs text-muted'>
            Before using any cleaning liquids or chemicals, please refer to the technical data sheets and safety data
            sheets available for download on our website at www.hexis-graphics.com.
          </p>

          <p className='font-medium text-foreground'>3.1. Clean and soiled surface appearance</p>
          <p>
            For vehicle wraps, it is advised to wash the vehicle with the SHAMPCARV2 vehicle body shampoo, then carry
            out a final cleaning using the SHAGCLEAN product.
          </p>

          <p className='font-medium text-foreground'>3.2. Heavily soiled surface appearance</p>
          <p>
            For vehicle wraps, it is advised to wash the vehicle with the SHAMPCARV2 vehicle body shampoo, then use
            the SHAGREMOV product.
          </p>
          <p className='text-xs text-muted'>
            Handle the SHAGREMOV product in a ventilated area. Wear protective goggles and gloves. Prior to
            treatment, run a compatibility test on a small, inconspicuous area of the substrate to be treated. Indeed,
            certain plastic materials might be damaged by the SHAGREMOV product.
          </p>
          <BulletList
            items={[
              'Spray the SHAGREMOV product onto the dirty surface and spread out using a dry cloth.',
              'Then wait for a few minutes. Spray the SHAGREMOV product again, then wipe it dry with a clean cloth or squeegee.',
              'When the substrate is clean and dry, carry out a final cleaning using the SHAGCLEAN products.'
            ]}
          />
        </DocSection>

        <DocSection id='ag-4-application' title='4. Application of the BODYFENCE Film'>
          <p>
            The recommended minimum application temperature is +10 °C (+50 °F). Preferably, apply at a temperature
            ranging from +15 °C to +35 °C (from +59 °F to +95 °F).
          </p>
          <p>
            The film must be applied using the ‘wet’ application method, either with the diluted HEXISOAP application
            solution or with our ready-to-use HEXISGEL product (for strongly deformed surfaces).
          </p>
          <BulletList
            items={[
              'To apply with the HEXISOAP application solution, prepare the PULVERISAT or SHAGSPRAY vaporiser with water, supplemented with 1 pump of HEXISOAP per litre of water. This mixture can be easily removed with the squeegee during installation.',
              'Measure the parts to be covered. Cut the BODYFENCE while leaving a margin of at least 10 cm (4 in.) around the surface to be covered in order to stretch it during application, and avoid any finger marks. (FIG. 01)',
              'Carefully separate the liner from the BODYFENCE. (FIG. 02)',
              'Spray the diluted HEXISOAP application solution or HEXISGEL on the part of the bodywork to be wrapped and on the adhesive-coated side of the BODYFENCE film (FIG. 03). This will enable a better positioning of the product on the vehicle.',
              <>
                Firmly stretch the BODYFENCE and position it (adhesive-coated side on the body) in order to wrap the
                structure. (FIG. 04)
                <ul className='list-disc space-y-1 pl-5 mt-1.5'>
                  <li>
                    Note: For an optimal wrapping of a concave or convex surface, it may be necessary to soften the
                    film using the STEAMERSC3 steam cleaner.
                  </li>
                </ul>
              </>,
              'Apply the film using one of the SHAGPAM or SHAGFENCEM squeegees, according to the size and the deformation level of the surface to be covered.',
              'For a better gliding of the squeegee, spray the diluted HEXISOAP application solution or HEXISGEL, depending on the level of deformation, on the BODYFENCE film. Then apply the film with the squeegee, while holding it at an angle of 45°. Press firmly to adhere the film to the surface and remove the application liquid. (FIG. 05)',
              'After all removal of the BODYFENCE for adjusting or positioning, spray the film and vehicle body before any reapplication.',
              'Considering the different existing paint qualities, it may be necessary to activate the adhesive strength by spraying the very concave and/or convex shapes with HEXISGEL.',
              'Once the BODYFENCE film is applied, cut as indicated in the following paragraphs.'
            ]}
          />

          <p className='font-medium text-foreground'>4.1. Straight cut with overlap</p>
          <p>To avoid scratching the paint, the cutter blade must always be parallel to the vehicle body.</p>
          <BulletList
            items={[
              'Use a cutter with a new blade.',
              'For carrying out the cutting, the cutter blade must be placed against the edge of the adjacent section. During cutting, make sure you always follow this edge by positioning the cutter towards the outside. (FIG. 06)',
              'After cutting, 2 to 3 mm (1 to 1½ in.) of the film should overlap the edge of the covered vehicle body.',
              'If there are no adjoining sections, cut the film surplus leaving a 2- to 3-mm (1- to 1½-in.) film overlap. (FIG. 07)',
              'Remove any excess film.',
              'To finish, run the squeegee over the cut. Incline the squeegee towards the thinner edge (FIG. 08). Firmly apply over the edges, round parts and neighbouring or opposed areas.',
              'Always cut the angles of the BODYFENCE film in rounded sections in order to ensure optimum adhesion of the cut film edges.'
            ]}
          />

          <p className='font-medium text-foreground'>4.2. Cut without overlap</p>
          <p>This method is used for a cut along a seal or any trim that cannot be disassembled.</p>
          <BulletList
            items={[
              'Use a cutter with a new blade.',
              'Trace with your finger the contours of the area. Lift the vinyl of the adjacent part and drag it into the hollow using a squeegee so as to mark the seal edge.',
              'For cutting, the cutter blade must be placed in flat position on the squeegee.',
              'The squeegee + cutter blade must be parallel to the body and perpendicular to the seal.',
              'Make the squeegee/cutter blade compound slide over the body to carry out the cutting by always maintaining this blade orientation.',
              'Remove any excess vinyl.',
              'Cut the angles of the BODYFENCE film in rounded sections in order to ensure optimum adhesion of the cut film edges.',
              'Finish off by firmly running the squeegee over the cut.'
            ]}
          />

          <p className='font-medium text-foreground'>4.3. Finishing</p>
          <p>
            If the film has been applied on a heavily deformed surface with a liquid other than HEXISGEL, the film
            edges should be rinsed with demineralised water (or tap water if demineralised water is not available) in
            order to increase the adhesive’s adhesion to the substrate more quickly.
          </p>
          <BulletList
            items={[
              'Place absorbent paper on the SHAGFENCEM or SHAGPAM squeegee. Apply pressure to the cut edges to remove as much as humidity as possible. (FIG. 09)',
              'Firmly apply the film with the squeegee to the edges, rounded sections and adjacent or opposite faces of the body. (FIG. 10)',
              'If wrinkles appear at the edge trim, press them with your finger for approximately 30 seconds. (FIG. 11)'
            ]}
          />
        </DocSection>

        <DocSection id='ag-5-film-treatment' title='5. Film Treatment'>
          <p>
            In order to keep your full wrap films clean for longer and make them easier to clean, HEXIS recommend
            treating them with the NANO SERUM by HEXIS as soon as you have finished the film application and then
            periodically. This surface treatment increases the hydrophobic protection of the film, it improves its
            non-stick effect against aqueous compounds and external contaminants, and durably maintains the aesthetic
            appearance of the BODYFENCE films.
          </p>
          <p>
            The NANO SERUM by HEXIS must be applied by a qualified professional in a clean, dust-free, well-ventilated
            place away from sunlight.
          </p>
          <p className='text-xs text-muted'>
            HEXIS tip: Please refer to the technical data sheet of the NANO SERUM by HEXIS for the details of the
            liquid’s application method.
          </p>
          <p className='text-xs text-muted'>
            Note: A slight change in the appearance of the film may be observed after treatment with the NANO SERUM
            by HEXIS, but this will not impair in any way the quality of the film. HEXIS consider this change in
            appearance to be normal, taking into account the addition of a NANO SERUM by HEXIS layer to the surface
            of the film, and will not accept any claim on this ground.
          </p>
          <p>
            The protection of the NANO SERUM by HEXIS is effective up to 36 months, depending on the vehicle’s
            exposure and maintenance conditions.
          </p>
          <p>
            The protective effect of the NANO SERUM by HEXIS may be impaired and reduced if the vehicle is frequently
            cleaned with automatic rollers in car wash stations. The ceramic treatment loses its effectiveness when
            the water-repellent effect becomes irregular on the surface of the film.
          </p>
          <p>
            The NANO SERUM by HEXIS is a technical product that requires a meticulous application by a qualified
            professional. HEXIS cannot be held responsible for any traces of application, demarcations, rings, etc.
            visible on the film resulting from incorrect application of the product.
          </p>
          <p>
            HEXIS decline all responsibility in the case of inappropriate use or failure to comply with the safety
            instructions for all their products.
          </p>
        </DocSection>

        <DocSection id='ag-6-cleaning-maintenance' title='6. Cleaning and Maintenance of the BODYFENCE Film'>
          <p>
            For optimum maintenance of the BODYFENCE film, HEXIS suggest to use their range of HEXIS cleaning agents
            included in the BODYFINISH kit, specially designed for total wraps.
          </p>
          <p>
            The BODYFENCE film can be cleaned in any conventional automatic car wash, using cleaning products and
            detergents used for professional maintenance of vehicles and advertising equipment.
          </p>
          <p>
            Nevertheless, for the use of high-pressure cleaners, respect the following procedure: apply medium water
            pressure at a minimum distance of 50 cm (20 in.) and a maximum water temperature of 35 °C (95 °F).
          </p>
          <p className='text-xs text-muted'>
            Caution: However, the film should not be cleaned within the first 48 hours following its application as
            this can affect the adhesive strength that may result in the film peeling off.
          </p>
          <p className='text-xs text-muted'>Caution: Solvents and corrosive detergents must not be used.</p>
          <p>
            HEXIS are not liable for any adhesive films cleaned with the unspecified additives from cleaning
            stations.
          </p>
          <p>
            Car washes: The additive products and the condition of the rotating brushes may impair the adhesive
            strength of the films. It is commonly admitted that after 10 car washes, the polyurethane paint becomes
            streaked; therefore, and in the same way, we are not accountable for these mechanical effects that may
            affect the film appearance.
          </p>
          <p className='text-xs text-muted'>
            HEXIS tip: Always carry out a test on a small area before cleaning the entire surface to be covered.
          </p>
        </DocSection>

        <DocSection id='ag-7-removal' title='7. Removal Procedure'>
          <p>The BODYFENCE film features a special adhesive designed to facilitate removal.</p>
          <BulletList
            items={[
              'Gently lift the corner of the film with the cutter without damaging the substrate.',
              'Subsequently proceed, by heating small areas, to peel off the film gently.',
              'The film should form an angle of 70° to 80° relative to the substrate surface to reduce the risk of leaving any adhesive on the substrate or tearing off the film.'
            ]}
          />
          <p className='text-xs text-muted'>An angle more or less wide or acute will cause the film to break more easily.</p>
          <BulletList
            items={[
              'Continue to peel off the film gently until it is completely removed while keeping a watchful eye on the heat applied, on the pulling angle of the film, and the pulling speed.',
              'If any adhesive remains on the substrate, take a cloth soaked with our SHAGREMOV product and rub the surface until all traces disappear.'
            ]}
          />
          <p className='text-xs text-muted'>
            Prior to treatment, run a compatibility test on a small, inconspicuous area of the substrate to be
            treated. Indeed, certain plastic materials might be damaged by the SHAGREMOV product. HEXIS are not
            liable for damages and degradations caused to the substrate by using incompatible products.
          </p>
          <p className='text-xs text-muted'>
            Before using any of our liquids, please refer to the technical data sheets available on our website at
            www.hexis-graphics.com.
          </p>
        </DocSection>

        <DocSection id='ag-disclaimer' title='Technical Disclaimer'>
          <p>
            For further technical information, please refer to the Technical Data Sheets available for free download
            from our website www.hexis-graphics.com, on the “Professionals” pages.
          </p>
          <p className='text-xs text-muted'>
            Due to the great variety of substrates and the growing number of new applications, the installer must
            check the suitability of the media for each application. All the published information does not however
            constitute a binding guarantee. The seller cannot be held liable for indirectly related damages and
            assumes no liability for claims that are higher than the replacement value of the purchased product. All
            specifications are subject to potential changes without prior notice. Our specifications are
            automatically updated on our website www.hexis-graphics.com.
          </p>
        </DocSection>
      </DisclosureGroup>
    </div>
  )
}

// ---------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------

export function HexisBodyfencexGlossDocs() {
  return (
    <div className='space-y-8'>
      {/* Technical Data Sheet: Gloss-only, no Satin/Matte-specific sheet was supplied */}
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Technical Data Sheet</h3>
        <p className='text-xs text-muted mb-3'>Document FTP.FPS.095.H 11.24, BODYFENCEX (glossy surface finish)</p>
        <DisclosureGroup allowsMultipleExpanded className='flex flex-col gap-3'>
          <DocSection id='tds-description' title='Product Description'>
            <p>
              BODYFENCEX is a transparent adhesive-coated film, which is self-healing at ambient temperature and
              specially developed for protecting car bodywork. Its excellent shock-absorbing properties protect your
              bodywork against chips caused by stones, insects, car doors, washing brushes, etc. Glossy surface
              finish.
            </p>
          </DocSection>

          <DocSection id='tds-film-features' title='Film Features'>
            <p className='text-xs text-muted'>Average values unless noted otherwise.</p>
            <KeyValueTable
              rows={[
                { label: 'Total thickness (film + adhesive)', value: '180 µm (7.1 mil) ± 10 % (indicative value)' },
                { label: 'Tensile strength', value: '> 100 N/25 mm (Method HEXNFX41021)' },
                { label: 'Elongation at break', value: '> 350 % (Method HEXNFX41021)' },
                { label: 'Shrinkage, 168 hours at 70 °C (158 °F)', value: '< 0.2 mm (Method HEXRET001)' }
              ]}
            />
          </DocSection>

          <DocSection id='tds-liner' title='Liner'>
            <p>Silicone-coated PET liner.</p>
          </DocSection>

          <DocSection id='tds-adhesive-properties' title='Adhesive Properties'>
            <p className='text-xs text-muted'>Measured average values at publication of the technical data sheet.</p>
            <KeyValueTable
              rows={[
                { label: 'Peel strength test at 180°, after 20 minutes of application', value: '15 N/25 mm (Method HEXFTM001)' },
                { label: 'Peel strength test at 180°, after 24 hours of application', value: '19 N/25 mm (Method HEXFTM001)' },
                { label: 'Initial tack', value: '11 N/25 mm (Method HEXFTM009)' },
                { label: 'Release', value: '0.1 N/25 mm (Method HEXFTM003)' }
              ]}
            />
          </DocSection>

          <DocSection id='tds-adhesive' title='Adhesive'>
            <BulletList
              items={[
                'Solvent-based acrylic adhesive.',
                'Immediate and permanent adhesion, optimal after 24 hours of contact.'
              ]}
            />
          </DocSection>

          <DocSection id='tds-users-instructions' title='User’s Instructions'>
            <BulletList
              items={[
                'Wet application method.',
                'Recommended minimum application temperature: +10 °C (+50 °F). Apply preferably between +15 °C and +35 °C (+59 °F and +95 °F).',
                'Operating temperature range: -40 °C to +90 °C (-40 °F to +194 °F).',
                <>
                  The BODYFENCEX film is intended for vehicle body protection. It can also serve as a protection
                  system for full wrap films (HX20000, non-structured HX30000 and HX30SCH00) that are used for smooth
                  and basic full wraps without relief decoration. The application of BODYFENCEX to full wrap films is
                  at the customer’s judgement. HEXIS recommend the use of pre-cut BODYFENCEX for this particular
                  application. As the adhesion of BODYFENCEX is permanent, its removal from a full wrap film can
                  damage the latter. HEXIS decline any responsibility in the case of deterioration of a full wrap
                  film caused by the application and removal of BODYFENCEX.
                </>,
                'Self-healing properties of the film at ambient temperatures: this phenomenon of reabsorbing scratches accelerates especially as the temperature increases.',
                'Cut the film to the size of the part to be wrapped, allowing +10 cm (4 in.) of extra margin.',
                'Apply to a surface that is clean and free from any traces of contaminants (dust, grease, wax, silicone, etc.). Particular care must be taken to clean the angles and periphery of the target surfaces in order to enable the film to adhere properly to the surface.',
                'In the case of an already painted substrate, self-adhesive media must only be applied to undamaged original paintwork. If the paintwork is not original and/or damaged, the application and the removal are at the judgement and risk of the installer.'
              ]}
            />
            <p className='font-medium text-foreground pt-2'>Operating Recommendations</p>
            <BulletList
              items={[
                'Conformable product developed for protecting bodywork, underbody, painted bumpers and rear-view mirror casings against chips caused by stones, insects and scratches.',
                'Excellent protection against vandalism (key scratches).',
                'To facilitate application, HEXIS have several types of squeegees in their range of accessories, ranging from softer to harder (plastic or felt).',
                'For more information on the application method of the BODYFENCEX film, please refer to its Application Guide available under the “Professionals” heading, in the “Solar and safety films” category on our website www.hexis-graphics.com.'
              ]}
            />
          </DocSection>

          <DocSection id='tds-storage' title='Storage'>
            <KeyValueTable
              rows={[
                { label: 'Storage period before use', value: '1 year' },
                { label: 'Storage area', value: 'in a dust-free environment' },
                { label: 'Storage method before use', value: 'in its original packaging' },
                { label: 'Orientation of rolls before use', value: 'vertically' }
              ]}
            />
          </DocSection>

          <DocSection id='tds-durability' title='Durability: Central European Climate'>
            <BulletList items={['Vertical exposure: up to 10 years (under normal road use conditions).']} />
            <p>
              Applied to a full wrap film with a durability of less than 10 years, BODYFENCEX will increase the
              durability against yellowing and cracking of the full wrap film by up to 1 year (within the limit of
              the BODYFENCEX durability).
            </p>
            <p>
              Lifespan depends on the type of substrate, substrate preparation, exposure conditions and nature and
              frequency of the cleaning. For instance, it is estimated that a south-facing exposure will divide the
              durability by two. The same applies to a horizontal exposure. In addition, elevated temperatures
              (above +50 °C) (+122 °F) or pollution significantly reduce this durability.
            </p>
            <p className='text-xs text-muted'>
              To find the indicative durabilities of the films for any other exposure and geographical area, please
              refer to the “Conversion rules for indicative durabilities according to geographical area” chart
              available under Durability, on the “Professionals” pages on our site www.hexis-graphics.com.
            </p>
          </DocSection>

          <DocSection id='tds-notes' title='Notes'>
            <p className='text-xs text-muted'>
              Due to the great variety of substrates and the growing number of new applications, the installer must
              check the suitability of the medium for each application. In case of doubt, request an expert
              evaluation of the compatibility of the film and the glass in question. All of the published
              information is based on measurements regularly performed in the laboratory.
            </p>
          </DocSection>
        </DisclosureGroup>
      </div>

      <WarrantySection />
      <ApplicationGuideSection />
    </div>
  )
}

export function HexisBodyfencexsSatinDocs() {
  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Technical Data Sheet</h3>
        <p className='text-sm text-foreground/80'>
          HEXIS did not supply a technical data sheet specific to the BODYFENCEXS Satin finish, so physical film
          specifications (thickness, tensile strength, and similar figures) are not shown here. The warranty and
          application guide below cover the BODYFENCE range as a whole, including BODYFENCEXS, and apply to this
          product.
        </p>
      </div>
      <WarrantySection />
      <ApplicationGuideSection />
    </div>
  )
}

export function HexisBodyfencexmUltraMatteDocs() {
  return (
    <div className='space-y-8'>
      <div>
        <h3 className='text-base font-semibold text-foreground mb-3'>Technical Data Sheet</h3>
        <p className='text-sm text-foreground/80'>
          HEXIS did not supply a technical data sheet specific to the BODYFENCEXM Matte finish, so physical film
          specifications (thickness, tensile strength, and similar figures) are not shown here. The warranty and
          application guide below cover the BODYFENCE range as a whole, including BODYFENCEXM, and apply to this
          product.
        </p>
      </div>
      <WarrantySection />
      <ApplicationGuideSection />
    </div>
  )
}
